import Typography from '@material-ui/core/Typography'
import { Skeleton, styled } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { useMemo } from 'react'
import type { ComponentTypes } from 'types/selectComponent'
import { getGenericComponentConfigs } from '../detail/content/TechnicalDetails'

const FullWidthWrapper = styled('div')({
  width: '100%',
})

const SectionCardTop = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: 25,
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,
  borderBottom: '1px solid ' + theme.palette.grey[130],
}))

const SectionCardBottom = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: 25,
  borderBottomLeftRadius: 8,
  borderBottomRightRadius: 8,
}))

const TableContainer = styled('div')({
  border: '1px solid #ccc',
  overflow: 'hidden',
  width: '100%',
})

const Table = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
})

const TableRow = styled('tr')({
  backgroundColor: '#f9f9f9',
  border: '1px solid #eee',
  borderCollapse: 'collapse',
  '&:nth-child(even)': {
    backgroundColor: 'white',
  },
})

const TableCell = styled('td')<{ isHeader: boolean }>(({ theme, isHeader }) => ({
  padding: '12px 16px',
  color: isHeader ? '#333' : '#555',
  fontWeight: isHeader ? 'bold' : 'normal',
  borderLeft: isHeader ? 'none' : '1px solid #ddd',
  textAlign: isHeader ? 'left' : 'right',
  width: '25%',
  border: '1px solid #eee',
  borderCollapse: 'collapse',
}))

const TechnicalDetails3 = ({
  productDetailData,
  componentType,
  quantityPerItem,
}: {
  productDetailData: string | null
  componentType: ComponentTypes
  quantityPerItem: number
}) => {
  const productSpecData = useMemo(() => (productDetailData ? JSON.parse(productDetailData) : {}), [productDetailData])
  const translate = useTranslate()
  const optionalDataConfig = useMemo(() => {
    const data: { label: string; value: string | number | unknown }[] = []
    if (quantityPerItem) {
      data.push({
        label: translate('Pallet Size'),
        value: quantityPerItem,
      })
    }
    if (productSpecData.mcs) {
      data.push({
        label: translate('MCS Reference'),
        value: productSpecData.mcs,
      })
    }
    return data
  }, [productSpecData.mcs, quantityPerItem])

  const customDataConfig = getGenericComponentConfigs(componentType, productSpecData)

  return (
    <TechnicalDetailsWrapper>
      {customDataConfig.map((config, index) => {
        const isEvenIndex = index % 2 === 0
        if (!isEvenIndex) return null

        const formattedValue = config.formatter?.format
          ? config.formatter.format(productSpecData[config.source])
          : productSpecData[config.source]

        const nextConfig = customDataConfig[index + 1]
        const nextFormattedValue =
          nextConfig && nextConfig.formatter?.format
            ? nextConfig.formatter.format(productSpecData[nextConfig.source])
            : productSpecData[nextConfig?.source]

        return (
          <TableRow>
            <TableCell isHeader={true}>{translate(config.label)}</TableCell>
            <TableCell isHeader={false}>
              {formattedValue} {config.unit}
            </TableCell>
            {nextConfig && (
              <>
                <TableCell isHeader={true}>{translate(nextConfig.label)}</TableCell>
                <TableCell isHeader={false}>
                  {nextFormattedValue} {nextConfig.unit}
                </TableCell>
              </>
            )}
          </TableRow>
        )
      })}

      {optionalDataConfig.length > 0 && (
        <tr style={{ backgroundColor: '#f9f9f9', border: '1px solid #eee', borderCollapse: 'collapse' }}>
          <TableCell isHeader={true}>{optionalDataConfig[0].label}</TableCell>

          <TableCell isHeader={false}>
            <>{optionalDataConfig[0].value}</>
          </TableCell>
          {optionalDataConfig[1] && (
            <>
              <TableCell isHeader={true}>
                <>{optionalDataConfig[1].label}</>
              </TableCell>

              <TableCell isHeader={false}>
                <>{optionalDataConfig[1].value}</>
              </TableCell>
            </>
          )}
        </tr>
      )}
    </TechnicalDetailsWrapper>
  )
}

const rows = Array.from({ length: 8 }, (_, index) => index)
const cols = Array.from({ length: 2 }, (_, index) => index)
export const TechnicalDetailsLoading = () => {
  return (
    <TechnicalDetailsWrapper>
      {rows.map((row) => (
        <TableRow>
          {cols.map((col) => (
            <>
              <TableCell isHeader={true}>
                <Skeleton width="100%" height={13} />
              </TableCell>
              <TableCell isHeader={false}>
                <Skeleton width="100%" height={13} />
              </TableCell>
            </>
          ))}
        </TableRow>
      ))}
    </TechnicalDetailsWrapper>
  )
}

const TechnicalDetailsWrapper = ({ children }: { children: React.ReactNode }) => {
  const translate = useTranslate()

  return (
    <FullWidthWrapper>
      <SectionCardTop>
        <Typography variant="h6">{translate('Technical Details')}</Typography>
      </SectionCardTop>
      <SectionCardBottom>
        <TableContainer>
          <Table>
            <tbody>{children}</tbody>
          </Table>
        </TableContainer>
      </SectionCardBottom>
    </FullWidthWrapper>
  )
}

export default TechnicalDetails3
